import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import api from '../../services/api';
import { useDateFormat } from '../../hooks/useDateFormat';
import { getUserTimezone, getTimezoneDisplayName } from '../../utils/dateUtils';
import { workingRuleTypeLabel } from '../../utils/workingCalendarLabels';
import { summarizeWorkingRule } from '../../utils/workingCalendarRuleSummary';
import { WORKING_RULE_TYPES } from '../UserWorkingCalendarPanel';
import {
  type RuleRow,
  cloneRuleRow,
  WorkingCalendarYearPreview,
  WorkingCalendarRuleEditDialog,
  FrozenRulesList,
  DualViewDialogTitle,
  SectionLabelWithHint,
  WORKING_CALENDAR_DUAL_VIEW_DIALOG_PAPER_PROPS,
  WORKING_CALENDAR_DUAL_VIEW_CONTENT_SX,
  WORKING_CALENDAR_DUAL_VIEW_GRID_SX,
  WORKING_CALENDAR_PREVIEW_PANEL_SX,
  WORKING_CALENDAR_RULES_SCROLL_SX
} from './workingCalendarDualViewShared';

type Props = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  userId: string;
  orgRulesFrozen: RuleRow[];
  onSaved: () => void;
};

const MemberMyWorkCalendarDialog: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  userId,
  orgRulesFrozen,
  onSaved
}) => {
  const { preferences } = useDateFormat();
  const userTz = getUserTimezone(preferences);
  const userTzLabel = getTimezoneDisplayName(userTz);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [myRules, setMyRules] = useState<RuleRow[]>([]);
  const [previewMap, setPreviewMap] = useState<Record<string, { working: boolean; expectedHours: number }>>({});
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const firstDayOfWeek = 1;

  const [ruleModalIndex, setRuleModalIndex] = useState<number | null>(null);
  const [ruleModalSource, setRuleModalSource] = useState<RuleRow | null>(null);
  const ruleModalIndexRef = useRef<number | null>(null);
  ruleModalIndexRef.current = ruleModalIndex;

  const loadPersonalRules = useCallback(async () => {
    const prRes = await api.getMemberPersonalWorkingRules(organizationId, userId);
    const pr = (prRes?.data ?? prRes) as Array<{
      sortOrder: number;
      ruleType: string;
      payload: unknown;
    }>;
    setMyRules(
      (Array.isArray(pr) ? pr : []).map((r, i) => ({
        sortOrder: i,
        ruleType: r.ruleType,
        payload: (r.payload as Record<string, unknown>) || {}
      }))
    );
  }, [organizationId, userId]);

  const loadPreview = useCallback(async () => {
    const from = `${viewYear}-01-01`;
    const to = `${viewYear}-12-31`;
    try {
      const res = await api.resolveWorkingCalendar(organizationId, {
        userId,
        from,
        to,
        timezone: userTz
      });
      const days = (res?.data?.days ?? res?.days) as Array<{
        date: string;
        working: boolean;
        expectedHours: number;
      }>;
      const m: Record<string, { working: boolean; expectedHours: number }> = {};
      (Array.isArray(days) ? days : []).forEach((d) => {
        m[d.date] = { working: d.working, expectedHours: d.expectedHours };
      });
      setPreviewMap(m);
    } catch {
      setPreviewMap({});
    }
  }, [organizationId, userId, viewYear, userTz]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setViewYear(new Date().getFullYear());
    (async () => {
      try {
        await loadPersonalRules();
        await loadPreview();
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        setError(err.response?.data?.message || 'Failed to load my work calendar');
      }
    })();
  }, [open, loadPersonalRules, loadPreview]);

  useEffect(() => {
    if (open) loadPreview();
  }, [open, viewYear, loadPreview]);

  const closeRuleModal = () => {
    setRuleModalIndex(null);
    setRuleModalSource(null);
  };

  const openRuleModal = (index: number) => {
    const r = myRules[index];
    if (!r) return;
    setRuleModalIndex(index);
    setRuleModalSource(cloneRuleRow(r));
  };

  const addRule = (ruleType: string) => {
    let payload: Record<string, unknown> = { kind: ruleType };
    if (ruleType === WORKING_RULE_TYPES.WEEKDAY_HOURS) {
      payload = { kind: 'WEEKDAY_HOURS', weekdays: [1, 2, 3, 4, 5], hours: 8 };
    } else if (ruleType === WORKING_RULE_TYPES.ANNUAL_OFF) {
      payload = {
        kind: 'ANNUAL_OFF',
        startMonth: 12,
        startDay: 25,
        endMonth: 12,
        endDay: 25
      };
    } else if (ruleType === WORKING_RULE_TYPES.DATE_OFF) {
      payload = { kind: 'DATE_OFF', date: '2026-01-01' };
    } else if (ruleType === WORKING_RULE_TYPES.DATE_HOURS) {
      payload = { kind: 'DATE_HOURS', date: '2026-01-01', hours: 4 };
    }
    const idx = myRules.length;
    const newRow: RuleRow = { sortOrder: idx, ruleType, payload };
    setMyRules((prev) => [...prev, newRow]);
    setRuleModalIndex(idx);
    setRuleModalSource(cloneRuleRow(newRow));
  };

  const commitRuleFromModal = (row: RuleRow) => {
    if (ruleModalIndex === null) return;
    setMyRules((prev) =>
      prev.map((r, i) => (i === ruleModalIndex ? { ...r, ruleType: row.ruleType, payload: row.payload } : r))
    );
  };

  const moveRule = (index: number, dir: -1 | 1) => {
    setMyRules((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const cp = [...prev];
      [cp[index], cp[j]] = [cp[j]!, cp[index]!];
      return cp;
    });
    setRuleModalIndex((cur) => {
      if (cur === null) return null;
      if (cur === index) return index + dir;
      if (cur === index + dir) return index;
      return cur;
    });
  };

  const removeRule = (index: number) => {
    const curModal = ruleModalIndexRef.current;
    if (curModal === index) closeRuleModal();
    else if (curModal !== null && curModal > index) setRuleModalIndex(curModal - 1);
    setMyRules((prev) => prev.filter((_, i) => i !== index));
  };

  const saveMyRules = async () => {
    setSaving(true);
    setError(null);
    try {
      const sorted = myRules.map((r, i) => ({
        sortOrder: i,
        ruleType: r.ruleType,
        payload: r.payload
      }));
      await api.putMemberPersonalWorkingRules(organizationId, userId, sorted);
      await loadPreview();
      onSaved();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={WORKING_CALENDAR_DUAL_VIEW_DIALOG_PAPER_PROPS}
      >
        <DualViewDialogTitle
          hint="Organization rules apply first, then your adjustments below. Save to refresh the year preview."
        >
          My work calendar
        </DualViewDialogTitle>
        <DialogContent dividers sx={WORKING_CALENDAR_DUAL_VIEW_CONTENT_SX}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={WORKING_CALENDAR_DUAL_VIEW_GRID_SX}>
            <Grid item xs={12} md={5} sx={WORKING_CALENDAR_PREVIEW_PANEL_SX}>
              <WorkingCalendarYearPreview
                viewYear={viewYear}
                onViewYearChange={setViewYear}
                previewMap={previewMap}
                firstDayOfWeek={firstDayOfWeek}
                timezoneLabel={userTzLabel}
                previewHelpExtra="Preview includes organization calendar, employment %, and your saved rules."
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: 380, md: 580 },
                flex: { md: '1 1 0' },
                maxWidth: { md: '58.333%' }
              }}
            >
              <Box sx={WORKING_CALENDAR_RULES_SCROLL_SX}>
                <FrozenRulesList rules={orgRulesFrozen} title="Organization rules (frozen)" />

                <SectionLabelWithHint
                  label="My adjustments (later wins)"
                  hint="Add rules on top of the organization calendar. They apply after employment %. Later rules override earlier ones on the same day."
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addRule(WORKING_RULE_TYPES.WEEKDAY_HOURS)}
                  >
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.WEEKDAY_HOURS)}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.ANNUAL_OFF)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.ANNUAL_OFF)}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.DATE_OFF)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.DATE_OFF)}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => addRule(WORKING_RULE_TYPES.DATE_HOURS)}>
                    + {workingRuleTypeLabel(WORKING_RULE_TYPES.DATE_HOURS)}
                  </Button>
                </Box>
                {myRules.map((r, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      py: 0.75,
                      px: 1,
                      mb: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                      <Box component="span" fontWeight={600}>
                        {i + 1}.
                      </Box>{' '}
                      {workingRuleTypeLabel(r.ruleType)}: {summarizeWorkingRule(r)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton size="small" onClick={() => moveRule(i, -1)} disabled={i === 0}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => moveRule(i, 1)}
                            disabled={i === myRules.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openRuleModal(i)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => removeRule(i)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
                <Button variant="contained" onClick={saveMyRules} disabled={saving} sx={{ mt: 1 }}>
                  Save my work calendar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <WorkingCalendarRuleEditDialog
        open={ruleModalIndex !== null && ruleModalSource !== null}
        source={ruleModalSource}
        canManage
        onClose={closeRuleModal}
        onSave={commitRuleFromModal}
      />
    </>
  );
};

export default MemberMyWorkCalendarDialog;
